import React, { useMemo, useEffect } from "react";
import {
  Container,
  Box,
  Stack,
  Image,
  Button,
  useMatches,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
// components
import { SurveyRichText } from "@components/survey-components/survey-rich-text/survey-rich-text";
import { LanguageSelectStandalone } from "@components/LanguageSelect/index";

// assets
import css from "./survey-flow-extra-views.module.css";
import mightierLogoSvg from "@assets/images/mightier-logo.svg";
// types
import type {
  SurveyConfig,
  Image as TSurveyImage,
} from "@neuromotion-inc/mightier-survey-engine";
import { type SurveyEngineIface } from "src/services/surveyHooks/useSurveyEngine";
import { PageHeader } from "@components/page-header/page-header";

export const SurveyFlowExtraView = React.memo<React.PropsWithChildren>(
  function SurveyFlowExtraView({ children }) {
    return (
      <Container className={css.container}>
        <Stack justify="center" align="center">
          <Image
            src={mightierLogoSvg}
            alt="mightier-logo"
            className={css["mightier-logo"]}
          />

          {children}
        </Stack>
      </Container>
    );
  },
);

interface SurveyFlowExtraViewsProps {
  surveyEngine: SurveyEngineIface;
}

/**
 * START PAGE
 */

interface SurveyFlowStartUIProps extends SurveyFlowExtraViewsProps {
  onPressStart: () => void;
  startPageConfig: SurveyConfig["titlePage"];
}

export const SurveyFlowStartUI = React.memo<SurveyFlowStartUIProps>(
  function SurveyFlowStartUI({ surveyEngine, onPressStart, startPageConfig }) {
    return (
      <SurveyFlowExtraView>
        {startPageConfig.description ? (
          <SurveyRichText
            mt="xl"
            className={css["description-text"]}
            text={startPageConfig.description}
            surveyEngine={surveyEngine}
          />
        ) : null}

        <Button
          mt="xl"
          onClick={onPressStart}
          data-testid="survey-start-button"
        >
          <SurveyRichText
            className={css["description-text"]}
            text={startPageConfig.startButton.copy}
            surveyEngine={surveyEngine}
          />
        </Button>

        <Box mt="xl">
          <LanguageSelectStandalone includeLabel={false} />
        </Box>
      </SurveyFlowExtraView>
    );
  },
);

/**
 * ENDING PAGE
 */

interface SurveyFlowEndUIProps extends SurveyFlowExtraViewsProps {
  endPageConfig: SurveyConfig["endingPage"];
}

const DEFAULT_END_PAGE_IMAGE: TSurveyImage = {
  url: "https://cdn1.mightier.com/survey_assets/intro-profile-survey-ending.gif",
  alt: "Survey submission loading animation",
};

export const SurveyFlowEndUI = React.memo<SurveyFlowEndUIProps>(
  function SurveyFlowEndUI({ surveyEngine, endPageConfig }) {
    const { i18n } = useTranslation("translation");
    // const isMobile = window.innerWidth <= 600;

    const isMobile = useMatches({
      base: true,
      sm: false,
    });

    // 4 seconds after rendering the end page UI, if there is a redirectUrl value then redirect to it
    useEffect(() => {
      const redirectConfig = endPageConfig.redirectConfig;
      if (!redirectConfig) {
        return;
      }
      const locale = i18n.language as keyof typeof redirectConfig.urlPerLocale;
      const timer = setTimeout(() => {
        window.location.href = redirectConfig.urlPerLocale[locale];
      }, redirectConfig.delaySeconds * 1000);

      return (): void => clearTimeout(timer);
    }, [endPageConfig.redirectConfig, i18n]);

    const mainImageConfig = useMemo<TSurveyImage>(() => {
      return endPageConfig.mainImage ?? DEFAULT_END_PAGE_IMAGE;
    }, [endPageConfig]);

    return (
      <div>
        {!isMobile ? <PageHeader /> : null}
        <Stack align="center" justify="center" pt="100px" px="md">
          <Image
            w={{ base: "40%", sm: "20%" }}
            alt={mainImageConfig.alt}
            src={mainImageConfig.url}
          />
          {endPageConfig.mainTextParagraphs.map((paragraph, idx) => {
            return (
              <SurveyRichText
                key={idx}
                mt={idx === 0 ? "xl" : ""}
                className={css["description-text"]}
                text={paragraph}
                surveyEngine={surveyEngine}
                data-testid={`survey-flow-end-text-${idx}`}
              />
            );
          })}
        </Stack>
      </div>
    );
  },
);
